'use client';

import { SortDirection } from '@/enums/sort';
import clsx from 'clsx';
import type { AriaAttributes, Key, ReactNode } from 'react';
import SortComponent from './Sort';
import Checkbox from './Checkox';
import { Sort } from '@/types/sort.type';
import { surfaceLayers } from '@/ui/overlay-layers';

export type TableCheckboxData = {
    checked: boolean;
    disabled: boolean;
};

export type TableColumn<T> = {
    key: keyof T;
    alias: string;
    sort?: SortDirection;
    render?: (val: T[keyof T], record: T) => ReactNode;
    className?: string;
    convertValue?: (val: T[keyof T], record: T) => T[keyof T];
};

type TableRowKey<T> = keyof T | ((record: T, index: number) => string | number);

export type TableProps<T> = {
    columns: TableColumn<T>[];
    data: T[];
    onSortChanged?: (column: keyof T, direction?: SortDirection) => void;
    currentSort?: Sort;
    pickedData?: unknown[];
    setPickedData?: (val: unknown[]) => void;
    onHeaderCheckboxChange?: (checked: boolean) => void;
    onCheckboxChange?: (checked: boolean, item: T) => void;
    onRowClick?: (record: T) => void;
    isHeaderChecked?: boolean;
    checkboxData?: TableCheckboxData[];
    headerSecondaryRow?: ReactNode;
    headerSelectionContent?: ReactNode;
    showHeaderCheckbox?: boolean;
    isSelectionSyncing?: boolean;
    rowKey?: TableRowKey<T>;
    tableId?: string;
    tableScope?: string;
};

const getNextSortDirection = (currentDirection?: SortDirection): SortDirection => {
    switch (currentDirection) {
        case SortDirection.ASC:
            return SortDirection.DESC;
        case SortDirection.DESC:
            return SortDirection.NONE;
        case SortDirection.NONE:
        default:
            return SortDirection.ASC;
    }
};

const getAriaSortValue = (currentDirection?: SortDirection): AriaAttributes['aria-sort'] => {
    switch (currentDirection) {
        case SortDirection.ASC:
            return 'ascending';
        case SortDirection.DESC:
            return 'descending';
        case SortDirection.NONE:
        default:
            return 'none';
    }
};

function Table<T>({
    columns,
    data,
    onSortChanged,
    currentSort,
    pickedData,
    onRowClick,
    onHeaderCheckboxChange,
    onCheckboxChange,
    isHeaderChecked,
    checkboxData,
    headerSecondaryRow,
    headerSelectionContent,
    showHeaderCheckbox = true,
    isSelectionSyncing = false,
    rowKey,
    tableId,
    tableScope,
}: TableProps<T>) {
    const stickyBaseStyle = { zIndex: surfaceLayers.surfaceStickyBase };
    const stickyRaisedStyle = { zIndex: surfaceLayers.surfaceStickyRaised };
    const resolveRowIdentity = (record: T, index: number): string => {
        if (typeof rowKey === 'function') {
            return String(rowKey(record, index));
        }

        if (rowKey) {
            const propertyValue = record[rowKey];
            if (typeof propertyValue === 'string' || typeof propertyValue === 'number') {
                return String(propertyValue);
            }
        }

        return String(index);
    };

    return (
        <table className="w-full min-w-full table-auto" data-table-id={tableId} data-table-scope={tableScope}>
            <thead className="sticky top-0 w-full bg-gray-50" style={stickyRaisedStyle}>
                <tr className="h-10 border-y border-slate-200 bg-gray-50">
                    {pickedData !== undefined && (
                        <th
                            className="sticky left-0 h-10 w-8 bg-gray-50 px-1 py-0 text-start align-middle lg:w-[1%]"
                            key="header-checkbox"
                            style={stickyRaisedStyle}
                        >
                            {headerSelectionContent ??
                                (showHeaderCheckbox && (
                                    <Checkbox
                                        isChecked={!!isHeaderChecked}
                                        setIsChecked={(value) => onHeaderCheckboxChange?.(value)}
                                        ariaLabel={
                                            tableId
                                                ? `Выбрать все строки таблицы ${tableId}`
                                                : 'Выбрать все строки таблицы'
                                        }
                                        className={clsx(isSelectionSyncing && 'opacity-50')}
                                    />
                                ))}
                        </th>
                    )}
                    {columns.map((c) => {
                        const currentDirection =
                            currentSort?.sortKey === c.key ? currentSort.sortOrder : SortDirection.NONE;
                        const isSortable = !!(c.sort && onSortChanged);

                        return (
                            <th
                                key={c.alias}
                                scope="col"
                                className={clsx(
                                    'h-10 px-1 py-0 text-start text-[12px] leading-5 align-middle',
                                    isSortable && 'cursor-pointer select-none',
                                )}
                                aria-sort={isSortable ? getAriaSortValue(currentDirection) : undefined}
                                data-sort-key={isSortable ? String(c.key) : undefined}
                                onClick={() => {
                                    if (!isSortable) return;
                                    onSortChanged?.(c.key, getNextSortDirection(currentDirection));
                                }}
                                onKeyDown={(event) => {
                                    if (!isSortable) return;
                                    if (event.key !== 'Enter' && event.key !== ' ') return;
                                    event.preventDefault();
                                    onSortChanged?.(c.key, getNextSortDirection(currentDirection));
                                }}
                                tabIndex={isSortable ? 0 : undefined}
                            >
                                <div className="flex items-center gap-1">
                                    <span className="whitespace-normal lg:whitespace-nowrap">{c.alias}</span>
                                    {c.sort && <SortComponent value={currentDirection} />}
                                </div>
                            </th>
                        );
                    })}
                </tr>
                {headerSecondaryRow}
            </thead>
            <tbody className="w-full">
                {data.map((elem, index) => {
                    const rowIdentity = resolveRowIdentity(elem, index);
                    const isRowChecked = !!checkboxData?.[index].checked;

                    return (
                        <tr
                            key={rowIdentity}
                            className="group h-10 cursor-pointer border-y border-slate-200 bg-white hover:bg-blue-50"
                            onClick={() => onRowClick?.(elem)}
                            data-row-id={rowIdentity}
                            data-state={isRowChecked ? 'selected' : 'idle'}
                        >
                            {pickedData && (
                                <td
                                    className="sticky left-0 h-10 w-8 bg-white px-1 py-0 align-middle group-hover:bg-blue-50 lg:w-[1%]"
                                    key={clsx(index, '-checkbox')}
                                    onClick={(e) => e.stopPropagation()}
                                    style={stickyBaseStyle}
                                >
                                    <Checkbox
                                        isChecked={isRowChecked}
                                        setIsChecked={(value) => onCheckboxChange?.(value, elem)}
                                        disabled={!!checkboxData?.[index].disabled}
                                        ariaLabel={`Выбрать строку ${rowIdentity}`}
                                        className={clsx(isSelectionSyncing && 'opacity-50')}
                                    />
                                </td>
                            )}
                            {columns.map((c) => {
                                const convertedValue = c.convertValue ? c.convertValue(elem[c.key], elem) : elem[c.key];

                                return (
                                    <td
                                        key={c.key as Key}
                                        className={clsx(
                                            'h-10 px-1 py-0 text-xs leading-5 whitespace-pre-line break-words [overflow-wrap:anywhere] align-middle lg:text-sm',
                                            c.className,
                                        )}
                                    >
                                        {c.render ? c.render(convertedValue, elem) : convertedValue?.toString()}
                                    </td>
                                );
                            })}
                        </tr>
                    );
                })}
            </tbody>
        </table>
    );
}

export default Table;
